/**
 * DoorLog — the door's own record of this shift: what this phone scanned,
 * what each scan did, and how much of the evening is still outside.
 *
 * THE JOURNAL IS THE WHOLE POINT, and it can only exist here. `Ticket`
 * records no refusal: its state machine is valid/redeemed/cancelled plus the
 * redeem/undo stamps (`packages/types/src/ticket.entity.ts`), so a scan this
 * door turned away writes nothing, anywhere, ever. Every verdict does pass
 * through the client as it happens, though (`verdict.state.ts`'s single
 * writer), which is what makes an in-memory, per-device, session-scoped ring
 * buffer the one place a refusal can be remembered at all. The limits are
 * real and stated rather than designed around: this record is lost on
 * reload, never leaves the device, is not an audit trail, and is not shared
 * with the door next to it.
 *
 * A RING BUFFER, not an unbounded list. A door phone runs a whole evening on
 * one page load; `JOURNAL_LIMIT` is the oldest scan the surface promises to
 * still be holding, and the arithmetic is deliberately in one place so the
 * promise is a number rather than an accident of memory pressure.
 *
 * `expected` DELIBERATELY EXCLUDES CANCELLED TICKETS. `doorTicketCounts`
 * (`../door-tickets.state.ts`) counts `total` as every ticket this door
 * admits, refunded ones included — so its "remaining" figure permanently
 * carries tickets nobody will ever present, and a door that sold 600 and
 * refunded 18 can never reach zero. A doorperson quotes this number to a
 * supervisor, so it is corrected here rather than annotated.
 *
 * THE NAMES ARRIVE AFTER THE SCAN THEY BELONG TO, so `recordScan` appends
 * and `nameScan` fills in. Both names are resolved by reads that deliberately
 * run beside the verdict rather than inside it — a door on a dead connection
 * still admits and still journals, and the name simply never arrives. Every
 * patch is keyed on `ticketId`, which is the whole race guard: a name can
 * only ever land on rows that resolved the very ticket it was read for, so a
 * read that settles two scans late is harmless rather than a name painted on
 * a stranger. A `null` half never overwrites a resolved one — the two halves
 * settle independently and in either order.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { doorTickets } from "../door-tickets.state.ts";
import type { Verdict } from "../verdict/verdict.state.ts";

/** The oldest scan this device still holds. A busy entrance resolves roughly
 *  a scan every three seconds at peak; 400 is over an hour of that, which is
 *  longer than any dispute survives. */
export const JOURNAL_LIMIT = 400;

/** Who a scanned ticket is for, and who paid for it — the two facts a
 *  doorperson needs to settle "my friend bought this for me", read from two
 *  different documents under two different grants. `holderName` is the live
 *  `Handover` recipient (`tickets.checkin.scan` + mfa, so every door has it);
 *  `buyerName` is the order's purchaser (`tickets.reports.view` + mfa, so a
 *  scan-only phone simply never resolves it and the row shows the half it
 *  has). Either may be `null` at any point in a scan's life: before its read
 *  settles, because the fact does not exist, or because this door may not
 *  read it — three states one row renders identically, since none of them is
 *  something the operator can act on. */
export type ScanNames = {
	readonly holderName: string | null;
	readonly buyerName: string | null;
};

/** One scan, as this door resolved it. The verdict rides whole rather than
 *  flattened, because it is already the canonical vocabulary for what a door
 *  can decide (`verdict.state.ts`'s own union) and a parallel summary of it
 *  would drift the moment a new reason is added.
 *
 *  `ticketId` sits beside the verdict rather than inside it: the union's
 *  `stop` and `held` members carry no ticket, and the printed reference off
 *  the ones that do is the only handle a scan-only door and a guest can
 *  match on. */
export type DoorScan = ScanNames & {
	readonly key: string;
	readonly atMillis: number;
	readonly ticketId: string | null;
	readonly verdict: Verdict;
};

export const doorJournal = state<readonly DoorScan[]>(
	"door-log/journal",
).memory([]);

/** Newest first, capped. The sole append path — a scan enters the journal
 *  here and nowhere else, so the cap cannot be forgotten at one call site. */
export function recordScan(scan: DoorScan): void {
	doorJournal.replace([scan, ...doorJournal.value].slice(0, JOURNAL_LIMIT));
}

/** The other half of the one write path: a name read that settled after the
 *  scan it belongs to, filled into every row that resolved this ticket —
 *  including an earlier already-in row for the same ticket, which is exactly
 *  the row a dispute is about. A `null` half is "nothing arrived on this
 *  side", never "clear what is there". */
export function nameScan(ticketId: string, names: ScanNames): void {
	doorJournal.replace(
		doorJournal.value.map((scan) =>
			scan.ticketId === ticketId
				? {
						...scan,
						holderName: names.holderName ?? scan.holderName,
						buyerName: names.buyerName ?? scan.buyerName,
					}
				: scan,
		),
	);
}

/** What this entrance has done and has left, with refunded tickets out of
 *  both figures — see this file's header. */
export const doorCounts = computed(() => {
	// No figure before the index has answered: a door that has not heard yet
	// has counted nothing, not zero.
	const tickets = doorTickets.get();
	if (tickets === null) return null;
	let admitted = 0;
	let toCome = 0;
	for (const ticket of tickets) {
		if (ticket.status === "redeemed") admitted += 1;
		else if (ticket.status === "valid") toCome += 1;
	}
	return { admitted, toCome, expected: admitted + toCome };
});

/** One entry per type this entrance admits — which lanes are still moving,
 *  the question the counts above cannot answer.
 *
 *  A lane with nothing left KEEPS its entry, and the order is fixed by how
 *  big the lane is rather than by how much of it is outstanding: both are the
 *  same decision, that a strip an operator glances at forty times a night
 *  must sit still. Sorted by what is outstanding, the entries reorder every
 *  few scans and the operator re-reads the whole line to find the lane they
 *  were watching; dropping an emptied lane does the same thing while also
 *  losing "this lane is done", which is itself a fact worth reading.
 *
 *  Ids only: resolving one to a name is a function of the reader's own
 *  language, which a pure state module has no import for. */
export const doorLanes = computed<
	readonly { typeId: string; toCome: number; expected: number }[]
>(() => {
	const byType = new Map<string, { toCome: number; expected: number }>();
	for (const ticket of doorTickets.get() ?? []) {
		if (ticket.status === "cancelled") continue;
		const lane = byType.get(ticket.typeId) ?? { toCome: 0, expected: 0 };
		lane.expected += 1;
		if (ticket.status === "valid") lane.toCome += 1;
		byType.set(ticket.typeId, lane);
	}
	return [...byType.entries()]
		.map(([typeId, lane]) => ({ typeId, ...lane }))
		.sort((a, b) => b.expected - a.expected);
});
